import React, { useCallback } from "react";
import i18n from "lib/i18n";
import PropTypes from "prop-types";
import { isEqual, pick } from "underscore";
import AddressDetails from "components/AddressDetails";
import { Typography, withStyles } from "@theconversation/ui";
import Dropdown from "components/MemoisedDropdown";
import TextField from "components/MemoisedTextField";
import FormFieldset from "components/FormFieldset";
import PubliclyRecogniseDonor from "components/PubliclyRecogniseDonor";

import Analytics from "lib/Analytics";


const track = field => (event) => {
  if (event.target.value.length) {
    Analytics.trackFormEvent("Donation Form", "Completed", field);
  } else {
    Analytics.trackFormEvent("Donation Form", "Skipped", field);
  }
};

const styles = {
  menu: {
    width: 200,
  },
};

const TITLES = {
  en: ["Mr", "Mrs", "Ms", "Dr", "Prof"],
  fr: ["M", "Mme", "Mlle", "Dr", "Pr"],
};
const LANGUAGES = Object.keys(TITLES);

const ContactDetails = ({
  active,
  address,
  city,
  classes,
  country,
  email,
  errors,
  homeCountryCode,
  lastName,
  name,
  onChange,
  postcode,
  publiclyRecogniseDonor,
  showAddressInputs,
  showOptionalAddress,
  state,
  title,
}) => {
  const trackName = useCallback(track("Name"));
  const trackLastName = useCallback(track("LastName"));
  const trackEmail = useCallback(track("Email"));

  const setAddressDetails = useCallback(
    addressDetails => onChange(addressDetails),
    [onChange]
  );

  const setRecogniseDonor = useCallback(() => {
    Analytics.trackFormEvent("Donation Form", "Completed", "Anonymous", !publiclyRecogniseDonor);
    onChange({ publiclyRecogniseDonor: !publiclyRecogniseDonor });
  }, [onChange, publiclyRecogniseDonor]);

  const setEmail = useCallback(
    event => onChange({ email: event.target.value }),
    [onChange]
  );

  const setTitle = useCallback((event) => {
    track("Title")(event);
    onChange({ title: event.target.value });
  }, [onChange]);

  const setName = useCallback(
    event => onChange({ name: event.target.value }),
    [onChange]
  );

  const setLastName = useCallback(
    event => onChange({ lastName: event.target.value }),
    [onChange]
  );

  const addressDetails = () => {
    if (showAddressInputs) {
      const addressErrors = pick(errors, "address", "city", "country", "postcode", "state");

      return (
        <AddressDetails
          active={active}
          address={address}
          city={city}
          country={country}
          errors={addressErrors}
          homeCountryCode={homeCountryCode}
          onChange={setAddressDetails}
          postcode={postcode}
          showOptionalAddress={showOptionalAddress}
          state={state}
        />
      );
    }

    return "";
  };

  // Work through the fallback list of languages till we find one which we have
  // a title list for.
  const language = i18n.firstAvailableLanguage(LANGUAGES);
  const titleOptions = TITLES[language].map(titleStr =>
    <option key={titleStr} value={titleStr}>{titleStr}</option>
  );

  return (
    <div id="contact-details">
      <FormFieldset>
        <Typography variant="h6">{i18n.t("donation.heading.contact_details")}</Typography>
      </FormFieldset>

      <FormFieldset>
        <Dropdown
          className={classes.menu}
          disabled={!active}
          id="donation_title"
          label={i18n.t("donation.placeholder.your_title")}
          name="donation[title]"
          native
          onChange={setTitle}
          value={title}
        >
          <option value="" />
          {titleOptions}
        </Dropdown>
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete="given-name"
          disabled={!active}
          error={!!errors.name}
          fullWidth
          helperText={errors.name}
          id="donation_name"
          label={i18n.t("donation.placeholder.your_name")}
          name="donation[name]"
          onBlur={trackName}
          onChange={setName}
          type="text"
          value={name}
        />
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.lastName}
          fullWidth
          helperText={errors.lastName}
          id="donation_last_name"
          label={i18n.t("donation.placeholder.your_last_name")}
          name="donation[last_name]"
          onBlur={trackLastName}
          onChange={setLastName}
          type="text"
          value={lastName}
        />
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.email}
          fullWidth
          helperText={errors.email}
          id="donation_email"
          label={i18n.t("donation.placeholder.your_email_address")}
          name="donation[email]"
          onBlur={trackEmail}
          onChange={setEmail}
          type="email"
          value={email}
        />
      </FormFieldset>

      {addressDetails()}

      <FormFieldset>
        <PubliclyRecogniseDonor
          active={active}
          onChange={setRecogniseDonor}
          publiclyRecogniseDonor={publiclyRecogniseDonor}
        />
      </FormFieldset>
    </div>
  );
};

ContactDetails.defaultProps = {
  active: true,
  address: "",
  city: "",
  classes: {
    menu: "",
  },
  country: "",
  email: "",
  errors: {},
  homeCountryCode: "AU",
  lastName: "",
  name: "",
  onChange: () => {},
  postcode: "",
  publiclyRecogniseDonor: false,
  showAddressInputs: false,
  showOptionalAddress: false,
  state: "",
  title: "",
};

ContactDetails.propTypes = {
  active: PropTypes.bool,
  address: PropTypes.string,
  city: PropTypes.string,
  classes: PropTypes.shape({
    menu: PropTypes.string,
  }),
  country: PropTypes.string,
  email: PropTypes.string,
  errors: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    postcode: PropTypes.string,
    state: PropTypes.string,
    title: PropTypes.string,
  }),
  homeCountryCode: PropTypes.string,
  lastName: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  postcode: PropTypes.string,
  publiclyRecogniseDonor: PropTypes.bool,
  showAddressInputs: PropTypes.bool,
  showOptionalAddress: PropTypes.bool,
  state: PropTypes.string,
  title: PropTypes.string,
};

export default withStyles(styles)(
  React.memo(
    ContactDetails,
    (prevProps, nextProps) => isEqual(prevProps, nextProps)
  )
);
